import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { strapiService } from '../../utils/api';
import AlertService from '../../utils/alertService';
import {
  Badge, Button, Card, EmptyState, KpiCard, Note, Progress, SkeletonPage, Spinner, Sym, usePageTitle,
} from '../../components/app2';
import PeriodPicker from '../payroll-processing/components/PeriodPicker';

/**
 * Sincronizar varias empresas de una vez.
 *
 * El paralelismo ya lo resolvió el orquestador: N solicitudes son N filas y el
 * latido las avanza de cinco en cinco. Lo delicado de esta pantalla es otra
 * cosa — que quien la use no dispare treinta solicitudes a ciegas.
 *
 * Por eso el flujo tiene DOS pasos y no uno: primero se ve exactamente qué va a
 * pasar con cada empresa (a quién se le pedirá, a quién se le recortará el rango
 * y a quién se saltará, con el motivo), y solo entonces se confirma. Es la única
 * forma honesta de lanzar algo irreversible: el SAT limita cuántas veces se
 * puede pedir un periodo DE POR VIDA, así que una solicitud de más no se
 * devuelve.
 */

const RESULTADO = {
  creada: { etiqueta: 'Se pedirá', tono: 'brand', icono: 'send' },
  ajustada: { etiqueta: 'Solo lo que falta', tono: 'warn', icono: 'content_cut' },
  omitida: { etiqueta: 'Se salta', tono: 'muted', icono: 'block' },
};

const fecha = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

const cuando = (iso) => (iso
  ? new Date(iso).toLocaleString('es-MX', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '—');

const FilaEmpresa = ({ item }) => {
  const cfg = RESULTADO[item.resultado] || RESULTADO.omitida;
  return (
    <div className="a2-trow" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ flex: '1 1 240px', minWidth: 0 }}>
        <span className="a2-tcell-strong a2-truncate" style={{ display: 'block' }}>{item.empresa}</span>
        <span className="a2-tcell-muted a2-truncate" style={{ display: 'block' }}>{item.motivo}</span>
      </span>
      {item.desde ? (
        <span className="a2-tcell a2-mono" style={{ flex: '0 0 auto' }}>
          {fecha(item.desde)} → {fecha(item.hasta)}
        </span>
      ) : null}
      <span style={{ flex: '0 0 auto' }}>
        <Badge tone={cfg.tono}>{cfg.etiqueta}</Badge>
      </span>
    </div>
  );
};

const FilaLote = ({ lote, onAbrir }) => {
  const avance = lote.total > 0
    ? Math.round(((lote.terminadas + lote.fallidas) / lote.total) * 100)
    : 0;

  return (
    <button
      type="button"
      className="a2-trow"
      onClick={() => onAbrir(lote)}
      style={{
        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ flex: '1 1 220px', minWidth: 0 }}>
          <span className="a2-tcell-strong a2-mono a2-truncate" style={{ display: 'block' }}>{lote.batch_id}</span>
          <span className="a2-tcell-muted a2-truncate" style={{ display: 'block' }}>
            {fecha(lote.desde)} → {fecha(lote.hasta)} · {cuando(lote.inicio)}
          </span>
        </span>
        <span className="a2-tcell" style={{ flex: '0 0 auto' }}>
          {lote.terminadas + lote.fallidas} de {lote.total}
          {lote.fallidas > 0 ? ` · ${lote.fallidas} con problema` : ''}
        </span>
        <span style={{ flex: '0 0 auto' }}>
          {lote.completo
            ? <Badge tone="ok">Terminado</Badge>
            : <Badge tone="brand">En curso</Badge>}
        </span>
      </div>
      <Progress value={avance} label={`Avance del lote ${lote.batch_id}`} style={{ marginTop: 10 }} />
    </button>
  );
};

const SyncMasiva = () => {
  const navigate = useNavigate();
  usePageTitle({ title: 'Sincronizar varias empresas', kicker: 'Operación' });

  const [empresas, setEmpresas] = useState([]);
  const [elegidas, setElegidas] = useState(new Set());
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [preview, setPreview] = useState(null);
  const [lote, setLote] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [calculando, setCalculando] = useState(false);
  const [encolando, setEncolando] = useState(false);
  const [barriendo, setBarriendo] = useState(false);
  const temporizador = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const [elegibles, historial] = await Promise.all([
        strapiService.satSync.empresasElegibles(),
        strapiService.satSync.lotes(),
      ]);
      setEmpresas(elegibles?.data || []);
      setLotes(historial?.data || []);
    } catch (e) {
      console.error('Error cargando el catálogo de lotes:', e);
      AlertService.error('No se pudo cargar', 'No fue posible leer las empresas ni los lotes.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Mientras un lote esté vivo se refresca solo: es lo que se está mirando.
  const hayVivos = lotes.some((l) => !l.completo);
  useEffect(() => {
    clearInterval(temporizador.current);
    if (!hayVivos) return undefined;
    temporizador.current = setInterval(cargar, 8000);
    return () => clearInterval(temporizador.current);
  }, [hayVivos, cargar]);

  const alternar = (id) => {
    setPreview(null);
    setElegidas((antes) => {
      const copia = new Set(antes);
      if (copia.has(id)) copia.delete(id); else copia.add(id);
      return copia;
    });
  };

  const todas = () => {
    setPreview(null);
    setElegidas((antes) => (antes.size === empresas.length ? new Set() : new Set(empresas.map((e) => e.id))));
  };

  const calcular = async () => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      AlertService.warning('Falta el periodo', 'Elige las fechas que quieres pedirle al SAT.');
      return;
    }
    if (elegidas.size === 0) {
      AlertService.warning('Falta elegir empresas', 'Marca al menos una empresa.');
      return;
    }
    try {
      setCalculando(true);
      const r = await strapiService.satSync.crearLote({
        companyIds: [...elegidas],
        dateFrom: dateRange.startDate,
        dateTo: dateRange.endDate,
        simular: true,
      });
      setPreview(r?.data || null);
    } catch (e) {
      console.error('Error simulando el lote:', e);
      AlertService.error('No se pudo calcular', 'No fue posible revisar qué se pediría.');
    } finally {
      setCalculando(false);
    }
  };

  const confirmar = async () => {
    if (!preview) return;
    const aPedir = preview.creadas + preview.ajustadas;
    if (aPedir === 0) {
      AlertService.info('No hay nada que pedir', 'Todas las empresas elegidas ya tienen ese periodo.');
      return;
    }

    const seguro = await AlertService.confirm(
      `Se pedirán ${aPedir} sincronizacion(es)`,
      `Cada una consume un cupo del SAT que no se recupera. Se saltarán ${preview.omitidas} empresa(s) `
      + 'que ya tienen el periodo o están sincronizando.',
      { confirmText: 'Sí, encolar el lote', cancelText: 'Cancelar' },
    );
    if (!seguro) return;

    try {
      setEncolando(true);
      const r = await strapiService.satSync.crearLote({
        companyIds: [...elegidas],
        dateFrom: dateRange.startDate,
        dateTo: dateRange.endDate,
      });
      setLote(r?.data || null);
      setPreview(null);
      AlertService.success(
        'Lote encolado',
        `${r?.data?.creadas ?? 0} completa(s), ${r?.data?.ajustadas ?? 0} ajustada(s). `
        + 'Corren en el servidor: puedes cerrar esta pantalla.',
      );
      await cargar();
    } catch (e) {
      console.error('Error creando el lote:', e);
      AlertService.error('No se pudo encolar', 'No fue posible crear el lote.');
    } finally {
      setEncolando(false);
    }
  };

  /**
   * El barrido de anoche, a mano.
   *
   * Existe para dos situaciones reales: que el servidor estuviera caído a las
   * 00:00 y esa noche se perdiera, o querer ver a quién se le pediría antes de
   * que llegue la hora. Es idempotente: lo que ya se pidió no se repite.
   */
  const correrBarrido = async () => {
    try {
      setBarriendo(true);
      const previo = await strapiService.satSync.barrido({ simular: true });
      const r = previo?.data;
      if (!r) return;

      if (r.creadas + r.ajustadas === 0) {
        AlertService.info(
          `Nada que pedir del ${r.desde} al ${r.dia}`,
          `Las ${r.elegibles} empresa(s) con e.firma ya tienen ese día.`,
        );
        return;
      }

      const seguro = await AlertService.confirm(
        `Se pedirá del ${r.desde} al ${r.dia} a ${r.creadas + r.ajustadas} empresa(s)`,
        `De ${r.elegibles} con e.firma, ${r.omitidas} ya lo tienen y se saltan. `
        + 'Cada solicitud consume un cupo del SAT que no se recupera.',
        { confirmText: 'Sí, correr el barrido', cancelText: 'Cancelar' },
      );
      if (!seguro) return;

      const hecho = await strapiService.satSync.barrido({ dia: r.dia });
      AlertService.success(
        'Barrido en marcha',
        `${hecho?.data?.creadas ?? 0} solicitud(es) encoladas para ${r.desde} → ${r.dia}.`,
      );
      await cargar();
    } catch (e) {
      console.error('Error corriendo el barrido:', e);
      AlertService.error('No se pudo correr', 'No fue posible lanzar el barrido.');
    } finally {
      setBarriendo(false);
    }
  };

  if (cargando) return <SkeletonPage kpis={2} rows={6} />;

  const resultado = lote || preview;

  return (
    <div className="a2-anim-screen" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Button variant="ghost" size="sm" icon="arrow_back" onClick={() => navigate('/app/sync')}>
          Sincronizaciones
        </Button>
      </div>

      <Note tone="info" icon="groups">
        <span>
          <strong style={{ color: 'var(--heading)' }}>Se piden de cinco en cinco.</strong>{' '}
          El servidor las va avanzando aunque cierres la pantalla, y antes de encolar nada
          puedes ver exactamente qué se le pedirá a cada empresa.
        </span>
      </Note>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <h3 className="a2-card-title" style={{ fontSize: '1.05rem' }}>Barrido de cada noche</h3>
            <p className="a2-card-sub">
              Todas las noches a las 00:00 (hora de Ciudad de México) se le pide a cada empresa
              con e.firma una ventana de los últimos días, no solo el de ayer: la ley da{' '}
              <strong>72 horas para timbrar</strong> un recibo, así que uno emitido anteayer
              puede aparecer mañana. Cada día se pide durante cuatro noches seguidas —cuesta un
              solo cupo del SAT, porque se piden juntos— y la última cae ya con la ventana
              cerrada. Aquí puedes lanzarlo a mano si una noche se perdió.
            </p>
          </div>
          <Button variant="outline" icon="nightlight" onClick={correrBarrido} loading={barriendo}>
            Correr el de anoche
          </Button>
        </div>
      </Card>

      <Card>
        <h3 className="a2-card-title" style={{ fontSize: '1.05rem' }}>1 · Qué periodo</h3>
        <p className="a2-card-sub" style={{ marginBottom: 14 }}>
          El mismo rango para todas. A quien ya lo tenga, se le saltará; a quien lo tenga a
          medias, se le pedirá solo lo que falta.
        </p>
        <PeriodPicker
          dateRange={dateRange}
          onChange={(next) => { setDateRange(next); setPreview(null); }}
          disabled={encolando}
        />
      </Card>

      <Card>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <h3 className="a2-card-title" style={{ fontSize: '1.05rem' }}>2 · Qué empresas</h3>
            <p className="a2-card-sub">
              Solo aparecen las activas con la e.firma completa: sin las tres piezas, el SAT
              rechaza la autenticación y lo único que se consigue es una fila fallida.
            </p>
          </div>
          <Button variant="outline" size="sm" icon="checklist" onClick={todas}>
            {elegidas.size === empresas.length ? 'Quitar todas' : 'Todas las elegibles'}
          </Button>
        </div>

        {empresas.length === 0 ? (
          <EmptyState
            icon="badge"
            title="Ninguna empresa tiene la e.firma completa"
            text="Hace falta el certificado, la llave y la contraseña para poder pedirle algo al SAT."
          />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%,260px), 1fr))', gap: 8, marginTop: 14 }}>
            {empresas.map((e) => {
              const marcada = elegidas.has(e.id);
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => alternar(e.id)}
                  aria-pressed={marcada}
                  data-empresa={e.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    borderRadius: 11, textAlign: 'left', cursor: 'pointer',
                    background: marcada ? 'var(--brand-soft)' : 'var(--surface-2)',
                    border: `1px solid ${marcada ? 'var(--brand)' : 'var(--line)'}`,
                  }}
                >
                  <Sym
                    name={marcada ? 'check_box' : 'check_box_outline_blank'}
                    size={19}
                    style={{ color: marcada ? 'var(--brand)' : 'var(--muted)', flexShrink: 0 }}
                  />
                  <span className="a2-tcell a2-truncate" style={{ minWidth: 0 }}>{e.name}</span>
                </button>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <Button icon="fact_check" onClick={calcular} loading={calculando} disabled={encolando}>
            Ver qué se pedirá
          </Button>
          <span className="a2-tcell-muted" style={{ alignSelf: 'center' }}>
            {elegidas.size} de {empresas.length} elegida(s)
          </span>
        </div>
      </Card>

      {resultado ? (
        <Card>
          <h3 className="a2-card-title" style={{ fontSize: '1.05rem' }}>
            {lote ? 'Lote encolado' : '3 · Esto es lo que pasará'}
          </h3>

          <div className="a2-grid-kpi" style={{ marginTop: 14 }}>
            <KpiCard icon="send" label="Se pedirá completo" value={resultado.creadas} />
            <KpiCard icon="content_cut" label="Solo lo que falta" value={resultado.ajustadas} />
            <KpiCard icon="block" label="Se saltan" value={resultado.omitidas} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 4, marginTop: 16 }}>
            {resultado.empresas.map((e) => <FilaEmpresa key={e.company_id} item={e} />)}
          </div>

          {!lote ? (
            <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
              <Button icon="rocket_launch" onClick={confirmar} loading={encolando}>
                Confirmar y encolar
              </Button>
              <Button variant="ghost" onClick={() => setPreview(null)} disabled={encolando}>
                Cancelar
              </Button>
            </div>
          ) : (
            <Note tone="ok" icon="task_alt" style={{ marginTop: 16 }}>
              <span>
                Lote <strong style={{ color: 'var(--heading)' }}>{lote.batch_id}</strong> en marcha.
                Puedes seguirlo aquí abajo o en el histórico de Sincronizaciones.
              </span>
            </Note>
          )}
        </Card>
      ) : null}

      <Card>
        <h3 className="a2-card-title" style={{ fontSize: '1.05rem' }}>Lotes anteriores</h3>
        <p className="a2-card-sub" style={{ marginBottom: 14 }}>
          Cada lote se lee como unidad: cuántas terminaron, cuántas tuvieron problemas y cuántos
          recibos nuevos trajo en total.
        </p>

        {lotes.length === 0 ? (
          <EmptyState icon="inventory" title="Todavía no hay lotes" text="Cuando lances uno, aparecerá aquí con su avance." />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 4 }}>
            {lotes.map((l) => (
              <FilaLote
                key={l.batch_id}
                lote={l}
                onAbrir={(x) => navigate(`/app/sync?batch=${encodeURIComponent(x.batch_id)}`)}
              />
            ))}
          </div>
        )}

        {hayVivos ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
            <Spinner size={14} />
            <span className="a2-tcell-muted">Se actualiza solo mientras haya lotes en curso.</span>
          </div>
        ) : null}
      </Card>
    </div>
  );
};

export default SyncMasiva;
